import React, { Fragment, ReactNode, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { UseDataProps } from './types';
import { AttributeValue, Choice, NotificationTarget } from '90.quickConnect.Models/models';
import { EntitySchemaAttributeTypes } from '90.quickConnect.Models/enums';
import { emailParse } from '10.quickConnect.app/components/domain/Declaration/helpers/emailParsers';
import { useLogger } from '80.quickConnect.Core/logger';
import { mapChoiceListFromString } from '90.quickConnect.Models/mappings';
import { isChoice } from '90.quickConnect.Models/guards';

const useData = (): UseDataProps => {
  const { t } = useTranslation('equipment');
  const logger = useLogger();

  const displayAttributeValues = useCallback(
    (attribute: AttributeValue): string | null => {
      const { value, type } = attribute;
      if (value === null) return null;

      switch (type) {
        case EntitySchemaAttributeTypes.Bool:
          return t(`entityData.value.${attribute.value}`);

        case EntitySchemaAttributeTypes.Email:
          if (typeof value === 'string') {
            const { selectedTargets } = emailParse(value, logger);
            const stringnifiedSelectedTargets = selectedTargets.reduce(
              (acc: string[], { target, alias }: NotificationTarget) => {
                const textToDisplay = alias && alias !== '' ? `${target} (${alias})` : target;

                return [...acc, textToDisplay];
              },
              [],
            );

            return stringnifiedSelectedTargets.join(', ');
          } else {
            return null;
          }

        case EntitySchemaAttributeTypes.Decimal:
        case EntitySchemaAttributeTypes.Int:
          return typeof value === 'string' && !isNaN(parseFloat(value))
            ? value
            : typeof value === 'number'
            ? value.toString()
            : null;

        case EntitySchemaAttributeTypes.Date:
          return typeof value === 'string' && !isNaN(Date.parse(value)) ? new Date(value).toLocaleDateString() : null;
        case EntitySchemaAttributeTypes.DateTime:
          return typeof value === 'string' && !isNaN(Date.parse(value)) ? new Date(value).toLocaleString() : null;

        case EntitySchemaAttributeTypes.SharedListHierarchical:
        case EntitySchemaAttributeTypes.SharedList:
          // Cas 1: Tableau (Choice[] ou string[])
          if (Array.isArray(value)) {
            if (value.length === 0) return null;

            // Sous-cas 1a: Tableau de Choice
            if (isChoice(value[0])) {
              const stringnifiedChoice = (value as Choice[]).reduce(
                (acc: string[], { label, value: val }: Choice) => [...acc, label || val],
                [],
              );
              return stringnifiedChoice.join(', ');
            }

            // Sous-cas 1b: Tableau de strings
            if (typeof value[0] === 'string') {
              return (value as string[]).join(', ');
            }
          }

          // Cas 2: Objet Choice unique
          if (isChoice(value)) {
            return value.label || value.value;
          }

          // Cas 3: String - peut être JSON de Choice/ChoiceList
          if (typeof value === 'string') {
            try {
              const choices = mapChoiceListFromString(value);
              if (choices && choices.length > 0) {
                const labels = choices.map((c) => c.label || c.value);
                return labels.join(', ');
              }
              // Fallback: retourner la string brute si le parsing échoue
              return value;
            } catch (error) {
              logger.trace('EntityData/hooks', `Failed to parse choice list from string: ${value}`);
              return value;
            }
          }

          return null;

        default:
          return typeof value === 'string' ? value : null;
      }
    },
    [logger, t],
  );

  return { displayAttributeValues };
};

export default useData;
